import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";

const STAGES = ["Lead", "Qualified", "Proposal", "Negotiation", "Won", "Lost"];

const STAGE_COLOR = {
  Lead: "#475569",
  Qualified: "#0b72b9",
  Proposal: "#b45309",
  Negotiation: "#6d28d9",
  Won: "#15803d",
  Lost: "#b91c1c",
};

function formatMoney(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function pct(num, den) {
  if (!den) return 0;
  return Math.round((num / den) * 100);
}

function MetricCard({ label, value, sub, accent }) {
  return (
    <div
      className="glass-card"
      style={{ padding: "20px 22px", borderTop: `3px solid ${accent || "var(--brand)"}` }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent || "var(--brand)", lineHeight: 1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 6 }}>{sub}</div>
      )}
    </div>
  );
}

function buildSourceRows(deals) {
  const map = new Map();
  for (const d of deals) {
    const key = d.source?.trim() || "Unknown";
    if (!map.has(key)) map.set(key, { source: key, total: 0, open: 0, won: 0, value: 0 });
    const row = map.get(key);
    row.total += 1;
    row.value += Number(d.estimatedValue || 0);
    if (d.stage === "Won") row.won += 1;
    if (d.stage !== "Won" && d.stage !== "Lost") row.open += 1;
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total);
}

function buildOwnerRows(deals) {
  const map = new Map();
  for (const d of deals) {
    const key = d.ownerUserName || "Unassigned";
    if (!map.has(key)) map.set(key, { owner: key, total: 0, open: 0, won: 0, openValue: 0 });
    const row = map.get(key);
    row.total += 1;
    if (d.stage === "Won") row.won += 1;
    if (d.stage !== "Won" && d.stage !== "Lost") {
      row.open += 1;
      row.openValue += Number(d.estimatedValue || 0);
    }
  }
  return Array.from(map.values()).sort((a, b) => b.openValue - a.openValue);
}

export default function CrmReportsPage() {
  const [summary, setSummary] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [sumRes, dealsRes] = await Promise.all([
        apiFetch("/api/crm/summary"),
        apiFetch("/api/crm/deals"),
      ]);
      if (!sumRes.ok || !dealsRes.ok) throw new Error("Could not load CRM data.");
      const [sumData, dealsData] = await Promise.all([sumRes.json(), dealsRes.json()]);
      setSummary(sumData);
      setDeals(Array.isArray(dealsData) ? dealsData : []);
    } catch (err) {
      setError(err?.message || "Could not load CRM reports.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const winRate = useMemo(() => {
    if (!summary) return 0;
    const closed = summary.wonDeals + summary.lostDeals;
    return pct(summary.wonDeals, closed);
  }, [summary]);

  const avgDealValue = useMemo(() => {
    if (!summary || !summary.openDeals) return 0;
    return Math.round(summary.openPipelineValue / summary.openDeals);
  }, [summary]);

  const maxStageCount = useMemo(() => {
    if (!summary?.dealsByStage) return 1;
    return Math.max(1, ...Object.values(summary.dealsByStage));
  }, [summary]);

  const sourceRows = useMemo(() => buildSourceRows(deals), [deals]);
  const ownerRows = useMemo(() => buildOwnerRows(deals), [deals]);

  if (loading) {
    return (
      <div>
        <PageHeader title="CRM Reports" subtitle="Pipeline performance, win rates, and activity insights." />
        <div style={{ padding: "24px 0" }}>
          {[1, 2, 3].map((i) => (
            <Loader key={i} type="skeleton" height={80} style={{ marginBottom: 16 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader title="CRM Reports" subtitle="Pipeline performance, win rates, and activity insights." />
        <div className="error-banner">{error}</div>
      </div>
    );
  }

  if (!summary || summary.totalDeals === 0) {
    return (
      <div>
        <PageHeader title="CRM Reports" subtitle="Pipeline performance, win rates, and activity insights." />
        <div className="glass-card" style={{ padding: 32 }}>
          <EmptyState
            title="No data yet"
            subtitle="Create deals in the Pipeline to see reports here."
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="CRM Reports"
        subtitle="Pipeline performance, win rates, and activity insights."
      />

      {/* Key metrics */}
      <div className="summary-grid" style={{ marginBottom: 20 }}>
        <MetricCard
          label="Win Rate"
          value={`${winRate}%`}
          sub={`${summary.wonDeals} won · ${summary.lostDeals} lost`}
          accent="#15803d"
        />
        <MetricCard
          label="Open Pipeline"
          value={formatMoney(summary.openPipelineValue)}
          sub={`${summary.openDeals} active deals`}
          accent="var(--brand)"
        />
        <MetricCard
          label="Revenue Closed"
          value={formatMoney(summary.wonValue)}
          sub={`${summary.wonDeals} won deals`}
          accent="#0b72b9"
        />
        <MetricCard
          label="Avg Deal Size"
          value={formatMoney(avgDealValue)}
          sub="Open deals only"
          accent="#6d28d9"
        />
        <MetricCard
          label="Overdue Follow-ups"
          value={summary.overdueFollowUps}
          sub={`${summary.followUpsDueToday} due today`}
          accent={summary.overdueFollowUps > 0 ? "#b91c1c" : "#15803d"}
        />
      </div>

      {/* Pipeline funnel */}
      <div className="glass-card" style={{ marginBottom: 20, padding: "24px 28px" }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>Pipeline Funnel</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {STAGES.map((stage) => {
            const count = summary.dealsByStage?.[stage] || 0;
            const barPct = pct(count, maxStageCount);
            const color = STAGE_COLOR[stage];
            return (
              <div key={stage} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 90, fontSize: 12, fontWeight: 600, color, flexShrink: 0 }}>
                  {stage}
                </div>
                <div style={{ flex: 1, height: 20, background: "var(--surface-2)", borderRadius: 4, overflow: "hidden" }}>
                  <div
                    style={{
                      height: "100%",
                      width: `${barPct}%`,
                      background: color,
                      borderRadius: 4,
                      minWidth: count > 0 ? 4 : 0,
                      transition: "width 0.4s ease",
                    }}
                  />
                </div>
                <div style={{ width: 32, fontSize: 13, fontWeight: 700, color, textAlign: "right", flexShrink: 0 }}>
                  {count}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        {/* By Source */}
        <div className="glass-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", fontWeight: 700, fontSize: 14 }}>
            By Source
          </div>
          {sourceRows.length === 0 ? (
            <div style={{ padding: 24, color: "var(--text-2)", fontSize: 13 }}>No source data.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  {["Source", "Deals", "Won", "Value"].map((col) => (
                    <th key={col} style={{ padding: "8px 16px", textAlign: col === "Value" ? "right" : "left", fontSize: 11, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sourceRows.map((row) => (
                  <tr key={row.source} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 600 }}>{row.source}</td>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: "var(--text-2)" }}>{row.total}</td>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: "#15803d", fontWeight: row.won > 0 ? 600 : 400 }}>{row.won || "—"}</td>
                    <td style={{ padding: "10px 16px", fontSize: 13, textAlign: "right" }}>{formatMoney(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* By Owner */}
        <div className="glass-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", fontWeight: 700, fontSize: 14 }}>
            By Owner
          </div>
          {ownerRows.length === 0 ? (
            <div style={{ padding: 24, color: "var(--text-2)", fontSize: 13 }}>No owner data.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  {["Owner", "Total", "Open", "Pipeline"].map((col) => (
                    <th key={col} style={{ padding: "8px 16px", textAlign: col === "Pipeline" ? "right" : "left", fontSize: 11, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ownerRows.map((row) => (
                  <tr key={row.owner} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 600 }}>{row.owner}</td>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: "var(--text-2)" }}>{row.total}</td>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: "var(--brand)", fontWeight: row.open > 0 ? 600 : 400 }}>{row.open || "—"}</td>
                    <td style={{ padding: "10px 16px", fontSize: 13, textAlign: "right", fontWeight: 600 }}>{formatMoney(row.openValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
